import type React from 'react';
import { Section, SectionHeader } from '@/components/ui/Section';
import { HandDrawnCircle } from '@/components/ui/decorative';

interface StepData {
  readonly num: number;
  readonly title: string;
  readonly description: string;
}

const STEPS: ReadonlyArray<StepData> = [
  {
    num: 1,
    title: 'Topic Briefing',
    description: 'Multi-faceted search matrices generated to uncover hidden angles.',
  },
  {
    num: 2,
    title: 'Deep Extraction',
    description: 'Insights validated and synthesized across verified sources.',
  },
  {
    num: 3,
    title: 'Architecting',
    description: 'Outline drafted for maximum logic and reading flow.',
  },
  {
    num: 4,
    title: 'Publication',
    description: 'High-authority draft, ready to edit and ship.',
  },
];

export const HowItWorks: React.FC = () => {
  return (
    <Section className="bg-aged-cream">
      <SectionHeader
        badge="Workflow"
        title="Simple workflow. Powerful results."
        description="Four steps from topic to publication-ready article."
      />

      {/* Horizontal timeline (md+) */}
      <div className="hidden md:block relative">
        {/* Rule running through the center of the circle row */}
        <div
          className="absolute h-px bg-aged-brass/30 z-0"
          style={{ top: '23px', left: '12.5%', right: '12.5%' }}
          aria-hidden="true"
        />

        <div className="relative grid grid-cols-4 gap-6">
          {STEPS.map((step) => (
            <div key={step.num} className="text-center">
              <div className="relative inline-block bg-aged-cream px-3">
                <HandDrawnCircle number={step.num} variant="dark" />
              </div>
              <h3 className="mt-7 font-display text-xl font-semibold text-charcoal">
                {step.title}
              </h3>
              <p className="mt-3 text-muted-warm text-sm leading-relaxed max-w-[14rem] mx-auto">
                {step.description}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* Vertical timeline (mobile) */}
      <ol className="md:hidden space-y-10">
        {STEPS.map((step) => (
          <li key={step.num} className="flex gap-5 items-start">
            <div className="shrink-0">
              <HandDrawnCircle number={step.num} variant="dark" />
            </div>
            <div>
              <h3 className="font-display text-xl font-semibold text-charcoal">
                {step.title}
              </h3>
              <p className="mt-2 text-muted-warm leading-relaxed">
                {step.description}
              </p>
            </div>
          </li>
        ))}
      </ol>
    </Section>
  );
};
